import { useCallback, useEffect, useMemo, useState } from 'react';
import { changesApi } from '../../../services/changesApi';
import type { ChangeTypePolicy } from '../../../types/changes';
import { policyFlagsForType } from '../changeTypePolicy';

interface Options {
  onlyActive?: boolean;
  enabled?: boolean;
}

export function useChangeTypePolicies(options: Options = {}) {
  const onlyActive = options.onlyActive ?? true;
  const enabled = options.enabled ?? true;
  const [policies, setPolicies] = useState<ChangeTypePolicy[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await changesApi.listTypePolicies({ only_active: onlyActive });
      setPolicies(data.items || []);
    } catch {
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [onlyActive]);

  useEffect(() => {
    if (!enabled) return;
    void load();
  }, [enabled, load]);

  const byCode = useMemo(() => {
    const map: Record<string, ChangeTypePolicy> = {};
    policies.forEach((item) => {
      map[item.code] = item;
    });
    return map;
  }, [policies]);

  const typeOptions = useMemo(
    () =>
      policies
        .filter((item) => item.is_active !== false)
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name, 'pt-BR'),
        )
        .map((item) => ({ value: item.code, label: item.name })),
    [policies],
  );

  const flagsFor = useCallback(
    (code: string) => policyFlagsForType(code, byCode),
    [byCode],
  );

  return { policies, byCode, typeOptions, flagsFor, loading, reload: load };
}
